<script>
import PriceCard from '@components/cards/new-subscription-card.vue'
import { mapState, mapGetters } from 'vuex'
import isEmpty from 'lodash/isEmpty'
import cloneDeep from 'lodash/cloneDeep'
import { pricingMixin } from '../mixins/pricingMixin'
import imageUrlBuilder from '@sanity/image-url'
import client from '@sanityConfig'
const builder = imageUrlBuilder(client)
export default {
  components: {
    PriceCard
  },
  mixins: [pricingMixin],
  data () {
    return {
      step: 1,
      selectedSubscription: null,
      selectedChallenge: null,
      loginForm: {
        email: '',
        password: ''
      },
      signupForm: {
        email: '',
        password: '',
        name: '',
        phone: ''
      },
      resetForm: {
        email: ''
      },
      loading: false,
      error: '',
      showResetPasswordForm: false,
      purchaseStatusMessage: ''
    }
  },
  computed: {
    ...mapState([
      'subscriptions',
      'settings',
      'selectedOrder',
      'authModalType',
      'user',
      'locale',
      'activeChallengePage'
    ]),
    ...mapGetters([
      'activeChallenge',
      'activeSubscription',
      'activeSubscriptionPlan',
      'hasNativeSubscription',
      'hasActiveNativeSubscription',
      'loggedIn',
      'primaryColorRgb'
    ]),
    siteName () {
      return this.settings && this.settings.siteName && this.settings.siteName
    },
    backgroundImage () {
      return (
        this.activeChallengePage?.challenge?.challengeImage ? builder.image(this.activeChallengePage.challenge.challengeImage)
          : this.settings?.newCheckout?.checkoutImage &&
        builder.image(this.settings.newCheckout.checkoutImage)
      )
    },
    subscriptionHeaderText () {
      return this.activeChallengePage?.challenge?.subscriptionHeaderText ||
        this.settings?.newCheckout?.subscriptionHeaderText
    },
    subscriptionSubText () {
      return this.activeChallengePage?.challenge?.subscriptionSubText ||
        this.settings?.newCheckout?.subscriptionSubText
    },
    subscriptionButtonText () {
      return this.activeChallengePage?.challenge?.subscriptionButtonText ||
        this.settings?.newCheckout?.subscriptionButtonText || 'Checkout'
    },
    challengeHeaderText () {
      return this.activeChallengePage?.challenge?.challengeHeaderText
    },
    challengeSubText () {
      return this.activeChallengePage?.challenge?.challengeSubText
    },
    challengeButtonText () {
      return this.activeChallengePage?.challenge?.challengeButtonText || 'Next Step'
    },
    plans () {
      const sortedPlans = cloneDeep(this.subscriptions[0]?.plans)
      return sortedPlans?.sort((a, b) => {
        return b.amount - a.amount
      })
    }
  },
  methods: {
    toggleLogin () {
      if (this.authModalType === 'signup') {
        return this.$store.commit('setAuthModalType', 'login')
      }
      if (this.authModalType === 'login') {
        return this.$store.commit('setAuthModalType', 'signup')
      }
    },
    toggleReset () {
      this.showResetPasswordForm = !this.showResetPasswordForm
    },
    goback () {
      if (this.step === 1) {
        this.$bvModal.hide('new-modal-signup')
      } else if (this.step === 3) {
        this.activeChallenge ? this.step -= 1 : this.step -= 2
      } else {
        this.step -= 1
      }
    },
    async next () {
      if (this.step === 1) {
        if (this.authModalType === 'signup') {
          const response = await this.signup()
          if (!response) {
            this.loading = false
            this.error = 'Email already registered!'
            return
          }
        }
        if (this.authModalType === 'login') {
          const response = await this.login()
          if (!response) {
            this.loading = false
            this.error = 'Email/password is wrong!'
            return
          }
        }
        this.activeChallenge ? this.step += 1 : this.step += 2
      } else if (this.step === 2) {
        this.step += 1
      } else if (this.step === 3) {
        if (this.selectedOrder.plan || this.hasActiveNativeSubscription) {
          this.purchaseSubscription()
        } else {
          this.error = 'please select a subscription'
        }
      }
    },
    isSelected (plan) {
      if (this.selectedSubscription && !isEmpty(this.selectedSubscription)) {
        return this.selectedSubscription.interval === plan.interval && this.selectedSubscription.interval_count === plan.interval_count
      } else {
        return this.isCurrent(plan)
      }
    },
    isChallengeSelected (challenge) {
      return challenge.id === this.selectedChallenge?.id
    },
    setSubscription (subscription) {
      this.selectedSubscription = subscription
      this.$store.commit('setSelectedOrder', { ...this.selectedOrder, plan: subscription.id })
    },
    setChallenge (challenge) {
      this.selectedChallenge = challenge
      this.$store.commit('setSelectedOrder', { ...this.selectedOrder, sku: challenge.id })
    },
    isCurrent (plan) {
      if ((
        this.activeSubscription &&
        this.activeSubscription.plan &&
        this.activeSubscription.plan.interval === plan.interval && this.activeSubscription.plan.interval_count === plan.interval_count) ||
        (this.hasNativeSubscription && this.hasActiveNativeSubscription)) {
        return true
      }
      return false
    },
    async purchaseSubscription () {
      this.loading = true
      window.Leanplum.track('Checkout',
        {
          action: 'attempted_checkout',
          user: this.user._id,
          price_id: this.selectedOrder?.plan,
          product_id: this.selectedOrder?.sku || ' '
        })
      await this.$store.dispatch('startSession',
        {
          token: this.user.token,
          prices: [this.selectedOrder.plan, this.selectedOrder.sku]
        })
        .catch(() => {
          this.purchaseStatusMessage = 'Error! We cannot redirect to  stripe session with $0 charge'
        })
      setTimeout(() => {
        this.purchaseStatusMessage = 'payment successful!'
      }, 3000)
      setTimeout(() => {
        this.$bvModal.hide('new-modal-signup')
      }, 5000)
      this.loading = false
    },
    upgrade () {
      const annual = this.subscriptions[0]?.plans.find((subscription) => subscription.interval === 'year')
      this.setSubscription(annual)
      this.next()
    },
    keepMyCurrentSubscription () {
      const current = this.activeSubscription.plan
      this.setSubscription(current)
      this.activeChallenge ? this.next() : this.$bvModal.hide('new-modal-signup')
    },
    continueToCheckout () {
      const current = this.activeSubscription.plan
      this.setSubscription(current)
      this.activeChallenge ? this.next() : this.$bvModal.hide('new-modal-signup')
    },
    async signup () {
      this.error = ''
      this.loading = true
      const response = await this.$store.dispatch('register', this.signupForm)
      if (response?.data?.success) {
        this.leanPlumUser({
          action: 'sign_up',
          user: response.data.user
        })
        this.loading = false
        return response
      }
    },
    async login () {
      this.error = ''
      this.loading = true
      const response = await this.$store.dispatch('login', this.loginForm)
      if (response?.data?.success) {
        this.leanPlumUser({
          action: 'sign_in',
          user: response.data.user
        })
        this.loading = false
        return response
      }
    },
    async resetPassword () {
      this.error = ''
      this.loading = true
      const response = await this.$store.dispatch('forgotPassword', this.resetForm.email)
      if (response.data.success) {
        // Logged in
        this.showResetPasswordForm = false
      } else {
        this.error = response.data.message
      }
      this.loading = false
    },
    leanPlumUser ({ action, user }) {
      window.Leanplum.setUserId(user._id)
      if (window.ga) {
        window.ga('set', 'userId', user._id)
      }
      window.Leanplum.setUserAttributes({
        _id: user._id,
        name: user.profile.name,
        email: user.email,
        trainer_id: user.trainer_id,
        'Preferred Language': this.locale
      })
      window.Leanplum.track('User', { action, source: 'gateway' })
    }
  },
  mounted () {
    if (this.loggedIn) {
      this.step = this.activeChallenge ? 2 : 3
    }
    if (this.selectedOrder) {
      this.selectedSubscription = this.subscriptions[0]?.plans.find((subscription) => subscription.id === this.selectedOrder.plan)
    } else if (this.activeSubscription) {
      this.selectedSubscription = this.activeSubscription.plan
      this.$store.commit('setSelectedOrder', { sku: this.activeChallenge?.plans[0].id, plan: this.activeSubscription.plan.id })
    }
  }
}
</script>

<template>
  <div
    class="signup-container"
    :style="{ '--primaryColor': `rgba(${primaryColorRgb}, ${1})` }"
  >
    <img v-if="backgroundImage" :src="backgroundImage" alt="partner-image" class="patner-image">
    <div
      class="signup-steps"
    >
      <div
        @click.stop="goback"
        class="back-step-wrapper"
      >
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:class="'back-step'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M18.823 10.862a.667.667 0 00-.943 0l-4.667 4.667a.667.667 0 000 .943l4.667 4.666a.667.667 0 00.943-.942L14.627 16l4.196-4.195a.667.667 0 000-.943z" fill="#F70555"/><rect x=".5" y=".5" width="31" height="31" rx="15.5" stroke="#F70555"/></svg>
      </div>
      <div
        class="steps"
      >
        <span class="step" :class="{colored: step >= 1}"></span>
        <span class="line" :class="{colored: step >= 2}"></span>
        <span class="step" :class="{colored: step >= 2}"></span>
        <span class="line" :class="{colored: step >= 3}"></span>
        <span class="step" :class="{colored: step >= 3}"></span>
        <span class="line"></span>
        <span class="step"></span>
      </div>
      <template v-if="step === 1">
        <template v-if="!showResetPasswordForm">
          <template v-if="authModalType === 'signup'">
            <h2 class="header">Create Your Account</h2>
            <form>
              <div class="form-group">
                <label for="name-signup">Full Name</label>
                <input
                  v-model="signupForm.name"
                  type="text"
                  class="form-control"
                  id="name-signup"
                  placeholder="Full Name">
              </div>
              <div class="form-group">
                <label for="email-signup">Email address</label>
                <input
                  v-model="signupForm.email"
                  type="email"
                  class="form-control"
                  id="email-signup"
                  aria-describedby="emailHelp"
                  placeholder="Enter email">
              </div>
              <div class="form-group">
                <label for="phone-signup">Phone Number</label>
                <input
                  v-model="signupForm.phone"
                  type="tel"
                  class="form-control"
                  id="phone-signup"
                  aria-describedby="phoneHelp"
                  placeholder="Enter phone">
              </div>
              <div class="form-group">
                <label for="password-signup">Password</label>
                <input
                  v-model="signupForm.password"
                  type="password"
                  class="form-control"
                  id="password-signup"
                  placeholder="Password">
              </div>
              <button
                type="submit"
                class="next-button"
                @click.prevent="next"
              >
              <span v-show="!loading">Next</span>
              <b-spinner v-show="loading" small type="grow"></b-spinner>
            </button>
              <p v-show="error" class="erorr">{{ error }}</p>
            </form>
            <div class="toggle-view-button">
              <p class="text">Already Registered</p>
              <p class="text toggle" @click.stop="toggleLogin">Sign In</p>
            </div>
          </template>
          <template v-if="authModalType === 'login'">
            <h2 class="header">Have an Account?</h2>
            <form>
              <div class="form-group">
                <label for="email-login">Email Address</label>
                <input
                  type="email"
                  class="form-control"
                  id="email-login"
                  aria-describedby="emailHelp"
                  placeholder="Enter email"
                  v-model="loginForm.email"
                >
              </div>
              <div class="form-group">
                <label for="password-login">Password</label>
                <input
                  type="password"
                  class="form-control"
                  id="password-login"
                  placeholder="Password"
                  v-model="loginForm.password"
                >
              </div>
              <p class="text reset" @click.stop="toggleReset">Reset Password</p>
              <button
                type="submit"
                class="next-button"
                @click.prevent="next"
              >
              <span v-show="!loading">Next</span>
              <b-spinner v-show="loading" small type="grow"></b-spinner>
            </button>
              <p v-show="error" class="erorr">{{ error }}</p>
            </form>
            <div class="toggle-view-button">
              <p class="text">Don't have an account?</p>
              <p class="text toggle" @click.stop="toggleLogin">Sign Up</p>
            </div>
          </template>
        </template>
        <template v-else>
          <h2 class="header">Reset Password</h2>
            <form>
              <div class="form-group">
                <label for="email-login">Email Address</label>
                <input
                  type="email"
                  class="form-control"
                  id="email-login"
                  aria-describedby="emailHelp"
                  placeholder="Enter email"
                  v-model="resetForm.email"
                >
              </div>
              <p class="text reset" @click.stop="toggleReset">Go Back</p>
              <button
                type="submit"
                class="next-button"
                @click.prevent="resetPassword"
              >
              <span v-show="!loading">Reset Password</span>
              <b-spinner v-show="loading" small type="grow"></b-spinner>
            </button>
              <p v-show="error" class="erorr">{{ error }}</p>
          </form>
        </template>
      </template>
      <template v-else-if="step === 2">
        <h2 class="header">{{challengeHeaderText}}</h2>
        <p class="sub-header">{{challengeSubText}}</p>
        <PriceCard
          v-for="challenge in activeChallenge.plans"
          :key="challenge.id"
          :hideSubscribedFlag="false"
          :subscription="challenge"
          :isSelected="isChallengeSelected(challenge)"
          :isCurrent="isCurrent(challenge)"
          @select-price="setChallenge(challenge)"
          :sub="false"
        />
        <button
          type="submit"
          class="next-button"
          @click.prevent="next"
        >{{challengeButtonText}}</button>
      </template>
      <template v-else-if="step === 3">
        <h2 class="header" v-if="activeSubscriptionPlan">It seems you already have the {{ $_getPlanNickName(activeSubscriptionPlan) === 'annually' ? 'annual' : $_getPlanNickName(activeSubscriptionPlan) }} plan</h2>
        <h2 class="header" v-else-if="hasActiveNativeSubscription">It looks like you’re already subscribed to the app. Tap continue to proceed to check out</h2>
        <h2 class="header" v-else>{{subscriptionHeaderText}}</h2>
        <p class="">{{subscriptionSubText}}</p>
        <PriceCard
          v-for="plan in plans"
          :hideSubscribedFlag="false"
          :key="plan.id"
          :subscription="plan"
          :isSelected="isSelected(plan)"
          :isCurrent="isCurrent(plan)"
          @select-price="setSubscription(plan)"
          :sub="true"
        />
        <button
          v-if="!activeSubscriptionPlan"
          type="submit"
          class="next-button"
          @click.prevent="next"
        >
        <span v-show="!loading">{{ `${hasActiveNativeSubscription ? `Continue` : subscriptionButtonText}` }}</span>
        <b-spinner v-show="loading" small type="grow"></b-spinner>
      </button>
        <template v-else>
          <button
          v-if="$_getPlanNickName(activeSubscriptionPlan) === 'annually'"
            type="submit"
            class="next-button"
            @click.prevent="continueToCheckout"
          >
          <span v-show="!loading">Continue to Checkout</span>
          <b-spinner v-show="loading" small type="grow"></b-spinner>
        </button>
          <template v-else>
            <button
              type="submit"
              class="next-button"
              @click.prevent="upgrade"
            >
            <span v-show="!loading">I want to upgrade</span>
            <b-spinner v-show="loading" small type="grow"></b-spinner>
          </button>
            <button
              type="submit"
              class="next-button secondary"
              @click.prevent="keepMyCurrentSubscription"
            >
            <span v-show="!loading">I don't like saving money</span>
            <b-spinner v-show="loading" small type="grow"></b-spinner>
          </button>
        </template>
      </template>
        <p v-show="purchaseStatusMessage">{{purchaseStatusMessage}}</p>
        <p v-show="error" class="erorr">{{ error }}</p>
      </template>
    </div>
  </div>
</template>

<style lang="scss">
  .signup-container {
    display: flex;
    flex-wrap: wrap;

    .patner-image {
      max-width: 100%;
      height: 100vh;
    }

    .signup-steps {
      display: flex;
      flex-direction: column;
      flex: 1;
      padding-top: 50px;
      padding-left: 80px;
      padding-right: 200px;
      .back-step {
        cursor: pointer;
        margin-bottom: 48px;
        rect {
          stroke: var(--primaryColor);
        }
        path {
          fill: var(--primaryColor);
        }
      }
      .header {
        margin-bottom: 24px;
      }
      .sub-header {
        margin-bottom: 48px;
      }
      .fine-print {
        margin-top: 48px;
        display: flex;
        flex-direction: column;
        color: rgba(0, 0, 0, 0.6);
      }
      .steps {
        display: flex;
        align-items: center;
        margin-bottom: 48px;
        .step {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #DBDDDD;
          &.colored {
            background: var(--primaryColor);
          }
        }
        .line {
          width: 64px;
          height: 2px;
          background: #DBDDDD;
          &.colored {
            background: var(--primaryColor);
          }
        }
      }
      .next-button {
        background: var(--primaryColor);
        border: none;
        border-radius: 200px;
        padding: 14px 0;
        width: 100%;
        margin: 40px 0;
        color: #fff;

        &.secondary {
          background: #fff;
          color: var(--primaryColor);
          border: 1px solid var(--primaryColor);
          margin-top: 0;
        }
      }
      .toggle-view-button {
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 24px;
        .text {
          margin: 0;
        }
        .toggle {
          cursor: pointer;
          margin-left: 12px;
          color: var(--primaryColor);
        }
      }
      .reset {
        color: var(--primaryColor);
        text-align: center;
      }
    }
    @media screen and (max-width: 1300px) {
      .signup-steps {
        padding-right: 80px;
      }
    }
    @media screen and (max-width: 1300px) {
      .signup-steps {
        padding-right: 40px;
        padding-left: 40px;
      }
    }
    @media screen and (max-width: 800px) {
      .patner-image {
        max-width: 100%;
        height: 100%;
        order: 2;
      }
      .header {
        font-size: 24px;
      }
      .sub-header {
        font-size: 14px;
      }
    }
  }
  .erorr {
    color: red;
  }
</style>
